import { useCallback, useEffect, useRef, useState } from "react";
import {
  LayoutDashboard, MessagesSquare, Inbox, BookOpen, Settings2, Code2,
  LogOut, Loader2, CheckCircle2, PauseCircle, PlayCircle, Search, Star,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";

async function pj(path, opts = {}) {
  const res = await fetch(`${API}/api/portal${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...opts,
  });
  if (res.status === 401 || res.status === 403) throw { auth: true };
  if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || "Fel");
  return res.json();
}

const TABS = [
  { id: "overview", label: "Översikt", icon: LayoutDashboard },
  { id: "conversations", label: "Konversationer", icon: MessagesSquare },
  { id: "leads", label: "Förfrågningar", icon: Inbox },
  { id: "knowledge", label: "Kunskap", icon: BookOpen },
  { id: "settings", label: "Inställningar", icon: Settings2 },
  { id: "install", label: "Installation", icon: Code2 },
];

// Kundvänd, enkel statusmodell för förfrågningar.
const LEAD_STATUSES = [
  { id: "ny", label: "Ny", active: "bg-[#255E69] text-white" },
  { id: "kontaktad", label: "Kontaktad", active: "bg-amber-500 text-white" },
  { id: "klar", label: "Klar", active: "bg-emerald-600 text-white" },
];

function LoginView() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await fetch(`${API}/api/portal/auth/magic-link`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      setSent(true);
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0e2a2f] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl p-8" data-testid="portal-login">
        <h1 className="text-2xl font-bold text-[#0e2a2f]">Kundportal</h1>
        <p className="text-sm text-gray-500 mt-1 mb-6">Logga in med din e-post — vi skickar en inloggningslänk.</p>
        {sent ? (
          <div className="flex items-start gap-3 bg-emerald-50 text-emerald-800 rounded-xl p-4" data-testid="portal-login-sent">
            <CheckCircle2 className="w-5 h-5 mt-0.5" />
            <p className="text-sm">Om du har åtkomst har vi skickat en inloggningslänk till <b>{email}</b>. Kolla din inkorg.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="din@epost.se" data-testid="portal-login-email"
              className="w-full border border-gray-300 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#255E69] outline-none"
            />
            <button type="submit" disabled={busy} data-testid="portal-login-submit"
              className="w-full bg-[#255E69] text-white rounded-xl py-3 font-semibold hover:bg-[#1d4b54] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-4 h-4 animate-spin" />} Skicka inloggningslänk
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid={`portal-stat-${label}`}>
      <div className="text-3xl font-bold text-[#0e2a2f]">{value}</div>
      <div className="text-sm text-gray-500 mt-1">{label}</div>
    </div>
  );
}

export default function CustomerPortal() {
  const [authed, setAuthed] = useState(null);
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState({});
  const [detail, setDetail] = useState(null);
  const [toast, setToast] = useState("");

  const boot = useCallback(async () => {
    try { setMe(await pj("/auth/me")); setAuthed(true); }
    catch { setAuthed(false); }
  }, []);
  useEffect(() => { boot(); }, [boot]);

  const loadTab = useCallback(async (t) => {
    try {
      if (t === "overview") { const r = await pj("/overview"); setData((d) => ({ ...d, overview: r })); }
      if (t === "conversations") { const r = await pj("/conversations"); setData((d) => ({ ...d, conversations: r.conversations })); }
      if (t === "leads") { const r = await pj("/leads"); setData((d) => ({ ...d, leads: r.leads })); }
      if (t === "knowledge") { const r = await pj("/knowledge"); setData((d) => ({ ...d, knowledge: r })); }
      if (t === "settings") { const r = await pj("/settings"); setData((d) => ({ ...d, settings: r })); }
      if (t === "install") { const r = await pj("/install"); setData((d) => ({ ...d, install: r })); }
    } catch (e) { if (e.auth) setAuthed(false); }
  }, []);
  useEffect(() => { if (authed) loadTab(tab); }, [authed, tab, loadTab]);

  const logout = async () => { await pj("/auth/logout", { method: "POST" }); setAuthed(false); };

  const setLeadStatus = async (id, status) => {
    setData((d) => ({ ...d, leads: (d.leads || []).map((l) => l.id === id ? { ...l, status } : l) }));
    try { await pj(`/leads/${id}`, { method: "PATCH", body: JSON.stringify({ status }) }); }
    catch (e) { if (e.auth) setAuthed(false); }
  };

  if (authed === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#255E69]" /></div>;
  if (!authed) return <LoginView />;

  return (
    <div className="min-h-screen bg-gray-50 flex" data-testid="portal-dashboard">
      <aside className="w-60 bg-[#0e2a2f] text-white flex flex-col">
        <div className="p-6 border-b border-white/10">
          <div className="text-xs text-white/50">Kundportal</div>
          <div className="font-bold truncate">{me?.company}</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => { setTab(t.id); setDetail(null); }}
              data-testid={`portal-tab-${t.id}`}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${tab === t.id ? "bg-white/15 font-semibold" : "text-white/70 hover:bg-white/10"}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </nav>
        <button onClick={logout} data-testid="portal-logout" className="m-3 flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-white/70 hover:bg-white/10">
          <LogOut className="w-4 h-4" /> Logga ut
        </button>
      </aside>

      <main className="flex-1 p-8 overflow-auto">
        <h1 className="text-2xl font-bold text-[#0e2a2f] mb-6">{TABS.find((t) => t.id === tab)?.label}</h1>

        {tab === "overview" && data.overview && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl">
            <Stat label="Konversationer" value={data.overview.stats.conversations} />
            <Stat label="Förfrågningar" value={data.overview.stats.leads} />
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center gap-2 text-lg font-semibold">
                {data.overview.agent_active ? <><PlayCircle className="w-5 h-5 text-emerald-500" /> Aktiv</> : <><PauseCircle className="w-5 h-5 text-amber-500" /> Pausad</>}
              </div>
              <div className="text-sm text-gray-500 mt-1">AI-agentens status</div>
            </div>
          </div>
        )}

        {tab === "conversations" && !detail && (
          <div className="space-y-2 max-w-3xl" data-testid="portal-conversations">
            {(data.conversations || []).map((c) => (
              <button key={c.session_id} onClick={async () => setDetail(await pj(`/conversations/${c.session_id}`))}
                className="w-full text-left bg-white rounded-xl border border-gray-100 p-4 hover:border-[#255E69] transition-colors">
                <div className="flex justify-between items-center">
                  <div className="font-medium text-gray-800 truncate">{c.summary || "Konversation"}</div>
                  <span className="text-xs text-gray-400 shrink-0 ml-3">{(c.created_at || "").slice(0, 10)}</span>
                </div>
                <div className="text-xs text-gray-500 mt-1">{c.message_count} meddelanden{c.became_lead ? " · blev förfrågan" : ""}</div>
              </button>
            ))}
            {(data.conversations || []).length === 0 && <p className="text-gray-400">Inga konversationer ännu.</p>}
          </div>
        )}
        {tab === "conversations" && detail && (
          <div className="max-w-2xl" data-testid="portal-conversation-detail">
            <button onClick={() => setDetail(null)} className="text-sm text-[#255E69] mb-4">← Tillbaka</button>
            <div className="space-y-3">
              {detail.messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "customer" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${m.role === "customer" ? "bg-[#255E69] text-white" : "bg-white border border-gray-100 text-gray-800"}`}>{m.content}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "leads" && (
          <div className="space-y-3 max-w-3xl" data-testid="portal-leads">
            {(data.leads || []).map((l) => (
              <div key={l.id} className="bg-white rounded-xl border border-gray-100 p-4" data-testid={`portal-lead-${l.id}`}>
                <div className="flex justify-between items-start gap-3">
                  <div className="font-semibold text-gray-800">{l.name || "Namnlös förfrågan"}</div>
                  <div className="flex gap-1 shrink-0" data-testid={`portal-lead-status-${l.id}`}>
                    {LEAD_STATUSES.map((s) => (
                      <button key={s.id} onClick={() => setLeadStatus(l.id, s.id)}
                        data-testid={`portal-lead-status-${l.id}-${s.id}`}
                        className={`text-xs px-2.5 py-1 rounded-full font-medium transition-colors ${l.status === s.id ? s.active : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}>
                        {s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="text-sm text-gray-600 mt-1">{[l.email, l.phone].filter(Boolean).join(" · ")}</div>
                {l.need && <p className="text-sm text-gray-500 mt-2 line-clamp-3">{l.need}</p>}
                <div className="text-xs text-gray-400 mt-2">{(l.created_at || "").slice(0, 10)}</div>
              </div>
            ))}
            {(data.leads || []).length === 0 && <p className="text-gray-400">Inga förfrågningar ännu.</p>}
          </div>
        )}

        {tab === "knowledge" && data.knowledge && (
          <div className="max-w-3xl space-y-4" data-testid="portal-knowledge">
            <PortalCatalog />
            <div className="bg-white rounded-xl border border-gray-100 divide-y divide-gray-50">
              {(data.knowledge.facts || []).map((f, i) => (
                <div key={i} className="p-4">
                  <div className="text-xs uppercase tracking-wide text-gray-400">{f.subject} · {f.label}</div>
                  <div className="text-gray-800 mt-0.5">{f.value}</div>
                </div>
              ))}
              {(data.knowledge.facts || []).length === 0 && <div className="p-4 text-gray-400">Inga verifierade fakta ännu.</div>}
            </div>
          </div>
        )}

        {tab === "settings" && data.settings && (
          <SettingsForm initial={data.settings} onSaved={(msg) => { setToast(msg); loadTab("settings"); setTimeout(() => setToast(""), 2500); }} />
        )}

        {tab === "install" && data.install && (
          <div className="max-w-2xl" data-testid="portal-install">
            <ol className="list-decimal list-inside text-gray-700 space-y-1 mb-4">
              {data.install.steps.map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            <pre className="bg-[#0e2a2f] text-emerald-200 rounded-xl p-4 text-xs overflow-auto">{data.install.snippet}</pre>
          </div>
        )}
        {toast && <div className="fixed bottom-6 right-6 bg-[#0e2a2f] text-white px-4 py-3 rounded-xl text-sm">{toast}</div>}
      </main>
    </div>
  );
}

function PortalCatalog() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const load = useCallback(async (qq, ff) => {
    setBusy(true); setErr("");
    try { setData(await pj(`/catalog/products?q=${encodeURIComponent(qq)}&filter=${ff}`)); }
    catch (e) { if (!e.auth) setErr("Kunde inte läsa produkter."); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load("", "all"); }, [load]);
  // Debounced sök: filtrerar medan man skriver (Enter/Sök-knappen fungerar
  // fortfarande omedelbart). Hoppar över första renderingen (initial load).
  const didMountRef = useRef(false);
  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return undefined; }
    const t = setTimeout(() => { load(q, filter); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);
  const toggle = async (p) => {
    const next = p.priority === "preferred" ? "normal" : "preferred";
    try {
      await pj("/catalog/focus", { method: "POST", body: JSON.stringify({ product_key: p.product_key, priority: next, sku: p.sku, ean: p.ean, product_name: p.product_name }) });
      await load(q, filter);
    } catch (e) { if (!e.auth) setErr("Kunde inte spara ändringen."); }
  };
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-3" data-testid="portal-catalog">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 text-[#0e2a2f] font-semibold"><BookOpen className="w-4 h-4" /> Produktkatalog</div>
        {data && (
          <div className="text-sm text-gray-500" data-testid="portal-catalog-count">
            <b className="text-[#0e2a2f]">{data.product_total}</b> produkter · <b className="text-amber-600" data-testid="portal-catalog-preferred-count">{data.preferred_count}</b> prioriterade
          </div>
        )}
      </div>
      <p className="text-xs text-gray-500">Markera de produkter du helst vill sälja som <b>Prioriterad</b> — AI-agenten lyfter dem först när de är relevanta.</p>
      <div className="flex gap-2 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
          <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load(q, filter)}
            placeholder="Sök produkt / SKU / EAN" data-testid="portal-catalog-search"
            className="w-full border border-gray-300 rounded-lg pl-8 pr-3 py-2 text-sm focus:ring-2 focus:ring-[#255E69] outline-none" />
        </div>
        <select value={filter} onChange={(e) => { setFilter(e.target.value); load(q, e.target.value); }} data-testid="portal-catalog-filter"
          className="border border-gray-300 rounded-lg px-2 py-2 text-sm">
          <option value="all">Alla</option>
          <option value="preferred">Prioriterade</option>
          <option value="normal">Normala</option>
          <option value="missing">Saknas i katalogen</option>
        </select>
        <button onClick={() => load(q, filter)} disabled={busy} data-testid="portal-catalog-refresh"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sök"}
        </button>
      </div>
      {err && <p className="text-sm text-red-600" data-testid="portal-catalog-error">{err}</p>}
      {data && data.product_total === 0 && !err && (
        <p className="text-sm text-gray-400" data-testid="portal-catalog-empty">Ingen aktiv produktkatalog ännu.</p>
      )}
      <div className="max-h-80 overflow-y-auto divide-y divide-gray-50">
        {(data?.products || []).map((p) => (
          <div key={p.product_key} className="flex items-center gap-3 py-2.5" data-testid={`portal-catalog-row-${p.product_key}`}>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-gray-800 truncate">{p.product_name || p.product_key}
                {!p.in_catalog && <span className="ml-1 text-[10px] font-semibold text-red-600">(saknas i katalog)</span>}
              </div>
              <div className="text-xs text-gray-400 truncate">
                {(p.category_path || []).join(" / ")}
                {p.preview_price != null ? ` · ${new Intl.NumberFormat("sv-SE").format(p.preview_price)} ${p.preview_currency || "kr"}` : ""}
                {p.sku ? ` · SKU ${p.sku}` : ""}
              </div>
            </div>
            <button onClick={() => toggle(p)} data-testid={`portal-catalog-toggle-${p.product_key}`}
              className={`shrink-0 flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${p.priority === "preferred" ? "bg-amber-500 text-white" : "border border-gray-300 text-gray-600 hover:bg-gray-50"}`}>
              <Star className={`w-3.5 h-3.5 ${p.priority === "preferred" ? "fill-white" : ""}`} />
              {p.priority === "preferred" ? "Prioriterad" : "Normal"}
            </button>
          </div>
        ))}
        {data && (data.products || []).length === 0 && data.product_total > 0 && (
          <p className="text-sm text-gray-400 py-3" data-testid="portal-catalog-no-match">Inga produkter för valt filter.</p>
        )}
      </div>
    </div>
  );
}

function SettingsForm({ initial, onSaved }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const upd = (k, v) => setF((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      await pj("/settings", { method: "PATCH", body: JSON.stringify(f) });
      onSaved("Sparat");
    } finally { setBusy(false); }
  };
  const Field = ({ label, children }) => (
    <div><label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>{children}</div>
  );
  return (
    <div className="max-w-lg space-y-5 bg-white rounded-xl border border-gray-100 p-6" data-testid="portal-settings">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-medium text-gray-800">AI-agent {f.active ? "aktiv" : "pausad"}</div>
          <div className="text-xs text-gray-500">Pausa för att tillfälligt stänga av chatten på din webbplats.</div>
        </div>
        <button onClick={() => upd("active", !f.active)} data-testid="portal-toggle-active"
          className={`px-4 py-2 rounded-lg text-sm font-semibold ${f.active ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-800"}`}>
          {f.active ? "Pausa" : "Aktivera"}
        </button>
      </div>
      <Field label="Starttext (första hälsning)">
        <textarea value={f.start_message} onChange={(e) => upd("start_message", e.target.value)} data-testid="portal-start-message"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" rows={2} />
      </Field>
      <Field label="Proaktiv text">
        <input value={f.proactive_message} onChange={(e) => upd("proactive_message", e.target.value)} data-testid="portal-proactive"
          className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
      </Field>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Färg">
          <input type="color" value={f.primary_color || "#255E69"} onChange={(e) => upd("primary_color", e.target.value)} data-testid="portal-color"
            className="w-full h-10 border border-gray-300 rounded-lg" />
        </Field>
        <Field label="Notifieringsmottagare (e-post)">
          <input value={f.notification_email} onChange={(e) => upd("notification_email", e.target.value)} data-testid="portal-notify-email"
            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" />
        </Field>
      </div>
      <button onClick={save} disabled={busy} data-testid="portal-settings-save"
        className="bg-[#255E69] text-white rounded-lg px-5 py-2.5 font-semibold hover:bg-[#1d4b54] disabled:opacity-60 flex items-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />} Spara
      </button>
    </div>
  );
}
